import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import ComposeEditor from '../components/ComposeEditor';
import RecipientUpload from '../components/RecipientUpload';
import TestSendButton from '../components/TestSendButton';
import { useAuth } from '../context/AuthContext';
import type { CsvParseResult } from '../lib/csv';
import { addRecipients, createCampaign, startCampaign } from '../services/campaigns';
import { importMailchimpCampaign } from '../services/importMailchimp';
import { cleanMailchimpHtml, summarizeMailchimpClean } from '../lib/mailchimpClean';
import { applyMerge, findUnknownTokens } from '../../functions/src/shared/merge';
import { htmlToText } from '../../functions/src/shared/htmlToText';
import { ALLOWED_SENDERS, DEFAULT_SENDER, findAllowedSender } from '../../functions/src/shared/senders';

const STEPS = ['Add recipients', 'Write the email', 'Review and send'];

/** Conservative default: 5/minute is far below any provider limit and reads as human. */
const DEFAULT_PER_MINUTE = 5;
const DEFAULT_DAILY_CAP = 1000;

export default function NewCampaignPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState('');
  const [fromEmail, setFromEmail] = useState(DEFAULT_SENDER.email);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [textOverride, setTextOverride] = useState('');
  const [csv, setCsv] = useState<CsvParseResult | null>(null);
  const [sendsPerMinute, setSendsPerMinute] = useState(DEFAULT_PER_MINUTE);
  const [dailyCap, setDailyCap] = useState(DEFAULT_DAILY_CAP);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Mailchimp import: when set, `html` is a rich Mailchimp design shown as a
  // read-only preview instead of the (text-oriented) TipTap editor.
  const [importedFromMailchimp, setImportedFromMailchimp] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importNotice, setImportNotice] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const mergeFields = csv?.fields ?? [];
  const recipients = csv?.rows ?? [];
  const selectedSender = findAllowedSender(fromEmail) ?? DEFAULT_SENDER;

  // Replies always go to the signed-in user; there is no Reply-To picker.
  const effectiveReplyTo = user?.email || '';

  const generatedText = useMemo(() => htmlToText(html), [html]);

  const unknownTokens = useMemo(
    () => findUnknownTokens(`${subject} ${html}`, mergeFields),
    [subject, html, mergeFields],
  );

  /** First uploaded row, so a test send exercises real merge values. */
  const sampleMergeData = recipients[0]?.mergeData;

  const preview = useMemo(() => {
    const sample = sampleMergeData ?? {};
    return {
      subject: applyMerge(subject, sample, false),
      html: applyMerge(html, sample, true),
      to: recipients[0]?.email ?? '',
    };
  }, [subject, html, recipients, sampleMergeData]);

  const estimate = useMemo(() => {
    if (recipients.length === 0) return '';
    const days = Math.ceil(recipients.length / Math.max(1, dailyCap));
    const minutesToday = Math.ceil(Math.min(recipients.length, dailyCap) / Math.max(1, sendsPerMinute));
    if (days > 1) {
      return `About ${days} days at ${dailyCap}/day (roughly ${minutesToday} minutes of sending each day).`;
    }
    return `About ${minutesToday} minute${minutesToday === 1 ? '' : 's'} of sending.`;
  }, [recipients.length, dailyCap, sendsPerMinute]);

  const stepIsComplete = [
    recipients.length > 0,
    Boolean(name.trim() && subject.trim() && html.replace(/<[^>]*>/g, '').trim()),
    true,
  ];

  async function handleImport() {
    const trimmed = importUrl.trim();
    if (!trimmed) return;
    setImporting(true);
    setImportError(null);
    setImportNotice(null);
    try {
      const { html: rawHtml, subject: importedSubject } = await importMailchimpCampaign(trimmed);
      const { html: cleaned, report } = cleanMailchimpHtml(rawHtml);
      setHtml(cleaned);
      setImportedFromMailchimp(true);
      if (importedSubject && !subject.trim()) setSubject(importedSubject);
      setImportNotice(summarizeMailchimpClean(report));
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Could not import that campaign.');
    } finally {
      setImporting(false);
    }
  }

  function clearImport() {
    setImportedFromMailchimp(false);
    setHtml('');
    setImportNotice(null);
    setImportError(null);
    setImportUrl('');
  }

  async function handleSend() {
    if (!user) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const campaignId = await createCampaign({
        name: name.trim(),
        subject: subject.trim(),
        html,
        text: textOverride.trim(),
        ownerUid: user.uid,
        fromEmail: selectedSender.email,
        fromName: selectedSender.name,
        replyTo: effectiveReplyTo,
        sendsPerMinute,
        dailyCap,
      });
      await addRecipients(campaignId, recipients);
      await startCampaign(campaignId);
      navigate(`/campaigns/${campaignId}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not start the campaign');
      setSubmitting(false);
    }
  }

  return (
    <Stack spacing={4}>
      <Typography variant="h5" component="h1">
        New campaign
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 1 && (
        <Stack spacing={3}>
          <TextField
            label="Campaign name"
            helperText="Internal only — recipients never see this."
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            fullWidth
          />
          <TextField
            select
            label="From"
            helperText="The address recipients see. Replies come back to you."
            value={fromEmail}
            onChange={(event) => setFromEmail(event.target.value)}
            fullWidth
          >
            {ALLOWED_SENDERS.map((sender) => (
              <MenuItem key={sender.email} value={sender.email}>
                {sender.name} — {sender.email}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Subject line"
            helperText="Merge fields work here too, e.g. Thanks for staying with us, {{name}}"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            required
            fullWidth
          />
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" component="h2" gutterBottom>
              Import from Mailchimp
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Designed this email in Mailchimp? In Mailchimp open the campaign, choose{' '}
              <strong>View email in browser</strong>, and paste that link here. We&apos;ll pull in
              the design, restore the real links, and strip Mailchimp&apos;s tracking.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
              <TextField
                label="Mailchimp campaign URL"
                placeholder="https://us1.campaign-archive.com/?u=…&id=…"
                value={importUrl}
                onChange={(event) => setImportUrl(event.target.value)}
                fullWidth
                type="url"
                inputProps={{ inputMode: 'url' }}
              />
              <Button
                variant="outlined"
                onClick={handleImport}
                disabled={importing || !importUrl.trim()}
                sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </Stack>
            {importError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setImportError(null)}>
                {importError}
              </Alert>
            )}
            {importNotice && (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setImportNotice(null)}>
                {importNotice}
              </Alert>
            )}
          </Paper>

          <Box>
            <Typography variant="subtitle1" component="h2" gutterBottom>
              Message
            </Typography>
            {importedFromMailchimp ? (
              <Paper variant="outlined">
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ p: 1, alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Imported Mailchimp design — sent exactly as shown. Edit it in Mailchimp and
                    re-import to change it.
                  </Typography>
                  <Button size="small" onClick={clearImport} sx={{ minHeight: 44, whiteSpace: 'nowrap' }}>
                    Clear &amp; write here instead
                  </Button>
                </Stack>
                <Divider />
                <Box
                  component="iframe"
                  title="Imported email preview"
                  srcDoc={html}
                  sandbox=""
                  sx={{ width: '100%', minHeight: 500, border: 0, display: 'block', bgcolor: '#fff' }}
                />
              </Paper>
            ) : (
              <ComposeEditor
                html={html}
                onChange={setHtml}
                mergeFields={mergeFields}
                showMergeFields
              />
            )}
          </Box>
          <TextField
            label="Plain text version"
            helperText="Generated from the message above. Edit only if you want the text-only version to differ."
            value={textOverride || generatedText}
            onChange={(event) => setTextOverride(event.target.value)}
            multiline
            minRows={4}
            fullWidth
          />
          <TestSendButton
            subject={subject}
            html={html}
            text={textOverride}
            fromEmail={selectedSender.email}
            fromName={selectedSender.name}
            replyTo={effectiveReplyTo}
            sampleMergeData={sampleMergeData}
            disabled={!stepIsComplete[0]}
          />
        </Stack>
      )}

      {activeStep === 0 && (
        <RecipientUpload result={csv} onParsed={(parsed) => setCsv(parsed)} />
      )}

      {activeStep === 2 && (
        <Stack spacing={3}>
          {unknownTokens.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>Unmatched merge fields</AlertTitle>
              No CSV column supplies {unknownTokens.map((token) => `{{${token}}}`).join(', ')}.
              These will be sent as blanks.
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" component="h2" gutterBottom>
              Sending as
            </Typography>
            <Typography variant="body2">
              {selectedSender.name} &lt;{selectedSender.email}&gt;
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Replies go to {effectiveReplyTo}.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" component="h2" gutterBottom>
              Pace
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Emails per minute"
                  type="number"
                  value={sendsPerMinute}
                  onChange={(event) =>
                    setSendsPerMinute(Math.max(1, Number(event.target.value) || 1))
                  }
                  slotProps={{ htmlInput: { min: 1, max: 60 } }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Daily cap"
                  type="number"
                  value={dailyCap}
                  onChange={(event) => setDailyCap(Math.max(1, Number(event.target.value) || 1))}
                  slotProps={{ htmlInput: { min: 1, max: 5000 } }}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {recipients.length} recipients. {estimate}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" component="h2" gutterBottom>
              Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As {preview.to || 'the first recipient'} will see it.
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              {preview.subject}
            </Typography>
            <Divider sx={{ my: 2 }} />
            {/* Body is sanitized on paste and again server-side before sending. */}
            <Box
              sx={{ '& img': { maxWidth: '100%' } }}
              dangerouslySetInnerHTML={{ __html: preview.html }}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              An unsubscribe link and the lodge's postal address are appended automatically.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <TestSendButton
              subject={subject}
              html={html}
              text={textOverride}
              fromEmail={selectedSender.email}
              fromName={selectedSender.name}
              replyTo={effectiveReplyTo}
              sampleMergeData={sampleMergeData}
            />
          </Paper>

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Stack>
      )}

      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button disabled={activeStep === 0 || submitting} onClick={() => setActiveStep(activeStep - 1)}>
          Back
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!stepIsComplete[activeStep]}
            onClick={() => setActiveStep(activeStep + 1)}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={submitting || recipients.length === 0}
            onClick={() => void handleSend()}
          >
            {submitting ? 'Starting…' : `Send to ${recipients.length}`}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
